import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { useMarketLink } from "../links";
import { useIsMobile, useV3 } from "../useV3";
import { Close } from "../Icons";
import { ConvList } from "./ConvList";
import { Conversation } from "./Conversation";
import { useThreads } from "./useThreads";

/* The chat dock: a bubble that opens a conversation without leaving the page.
 *
 * DESKTOP OPENS A DOCK, NEVER A PAGE. At 1440 the design is a 400×620 popup
 * anchored bottom-end; at 390 it is a full-screen sheet. Those are the
 * design's two components -- `sq-chat-popup` and `sq-chat-sheet` -- and the
 * viewport picks one. Reading a listing and answering a message are not
 * supposed to be the same act of navigation.
 *
 * IT HIDES WHERE THE INBOX ALREADY IS, per the design's own note on the
 * collapsed state. Two views of the same thread on one screen is a bug you
 * find by replying in the wrong one.
 *
 * THE COUNT ON THE BUBBLE IS THE NUMBER OF CONVERSATIONS, not of unread
 * messages. There is no read state on the server, so an "unread" badge would
 * be a number we made up. Labelled accordingly.
 */
/* Where the dock does NOT belong.
 *
 * The listing wizard is a form with a sticky action bar at the bottom; a
 * floating button parked in the same corner covers it, and on a 390 screen it
 * lands on «التالي». A visitor mid-way through posting a property is not
 * looking for their inbox. */
const DOCK_FREE = [/\/list-property(\/|$)/];

export function ChatDock() {
  const { s } = useV3();
  const mobile = useIsMobile();
  const { profile } = useAuth();
  const link = useMarketLink();
  const location = useLocation();
  const chat = useThreads(s.chat.conversation, s.chat.deletedUser);
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  /* Escape closes, like every other overlay on the site. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const onInbox = /\/account\/inbox\/?$/.test(location.pathname);
  /* …and out of the wizard, where a floating button in the bottom corner sits
     on top of the sticky «التالي». */
  const inWizard = DOCK_FREE.some((re) => re.test(location.pathname));
  if (!profile || onInbox || inWizard) return null;

  const threads = chat.threads ?? [];
  const current = threads.find((t) => t.id === openId) ?? null;

  if (!open) {
    return (
      <button type="button" className="sq-chat-fab" onClick={() => setOpen(true)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 5h16v11H8l-4 4z" />
        </svg>
        {s.chat.open}
        {threads.length > 0 && <span className="sq-chat-fab__count">{threads.length}</span>}
      </button>
    );
  }

  const head = (
    <div className="sq-chat-popup__head">
      {current && (
        <button type="button" className="sq-btn sq-btn--ghost sq-btn--icon"
          aria-label={s.chat.back} onClick={() => setOpenId(null)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
      <div className="sq-chat-popup__title">
        {current && <span className="sq-avatar" style={{ width: 36, height: 36 }} aria-hidden="true" />}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {current ? chat.peerName(current) : s.chat.title}
          </div>
        </div>
      </div>
      {/* Expanding to the full inbox is the design's own affordance. */}
      <Link to={link("/account/inbox")} className="sq-link" style={{ fontSize: 13 }}
        onClick={() => setOpen(false)}>
        {s.account.allMessages}
      </Link>
      <button type="button" className="sq-btn sq-btn--ghost sq-btn--icon"
        aria-label={s.chat.close} onClick={() => setOpen(false)}>
        <Close size={18} />
      </button>
    </div>
  );

  const body = chat.loading ? (
    <div style={{ padding: 16, display: "grid", gap: 10 }} aria-busy="true">
      {[0, 1, 2].map((i) => <div key={i} className="sq-skeleton" style={{ height: 56 }} />)}
    </div>
  ) : chat.failed ? (
    <p className="sq-thread__blocked" role="alert">{s.chat.error}</p>
  ) : current ? (
    <Conversation chat={chat} thread={current} />
  ) : (
    <ConvList chat={chat} threads={threads} openId={openId} onOpen={setOpenId} />
  );

  return (
    /* `data-component` is the design's own name for this element, carried so
       the acceptance harness can line the two up. */
    <div data-component={mobile ? "chat-sheet" : "chat-popup"}
      className={mobile ? "sq-chat-sheet" : "sq-chat-popup"}
      role="dialog" aria-modal={mobile ? "true" : undefined} aria-label={s.chat.title}>
      {head}
      {body}
    </div>
  );
}
